import { DocumentationLegacyPageBlockColumnItem } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockColumnItem";
import { DocumentationLegacyPageBlockTabItem } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockTabItem";
import { DocumentationLegacyPageBlockTableRow } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockTableRow";
import { DocumentationLegacyCalloutType } from "../../model/enums/SDKDocumentationLegacyCalloutType";
import { DocumentationLegacyHeadingType } from "../../model/enums/SDKDocumentationLegacyHeadingType";
import { DocumentationLegacyPageBlockType } from "../../model/enums/SDKDocumentationLegacyPageBlockType";
import { MarkdownLinkResolver } from "./SDKToolsMarkdownLinkResolver";
import { MarkdownTransformType } from "./SDKToolsMarkdownTransform";
import { MarkdownTransformUtil } from "./SDKToolsMarkdownTransformUtil";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Search Instance
/** Markdown block transformer */
export class MarkdownTransformBlock {
    // --- Conversion
    constructor(type, instance, designSystemId, workspaceId, versionId, config = { urlType: "relative-to-markdown" } // relative-to-markdown as default for backward compatibility
    ) {
        this.linkResolver = null;
        this.documentationData = null;
        this.transformType = type;
        this.instance = instance;
        this.designSystemId = designSystemId;
        this.versionId = versionId;
        this.workspaceId = workspaceId;
        this.config = config;
        // If documentation data is provided in config, use it
        if (config.documentationData) {
            this.documentationData = config.documentationData;
        }
        if (type === MarkdownTransformType.github) {
            console.log("Note: GitHub mode of markdown is currently in development and is not yet fully supported");
        }
        this.utilTransformer = new MarkdownTransformUtil();
    }
    /** Initialize documentation representation and link resolver - should be called once before conversion */
    async initialize() {
        // If we don't have documentation representation, fetch it
        if (!this.documentationData) {
            this.documentationData =
                await this.instance.documentation.getFullDocumentationLegacyRepresentation({
                    workspaceId: this.workspaceId,
                    designSystemId: this.designSystemId,
                    versionId: this.versionId,
                });
        }
        // Create link resolver if we don't have one yet (whether documentation was provided or fetched)
        if (!this.linkResolver && this.documentationData) {
            this.linkResolver = new MarkdownLinkResolver(this.documentationData, this.config, this.instance, this.designSystemId, this.versionId);
            // Update util transformer with link resolver
            this.utilTransformer = new MarkdownTransformUtil(this.linkResolver);
        }
    }
    // --- Conversion
    /** Converts a single block - depending on the type - to a markdown definition of specific type */
    async convertBlockToMarkdown(block, pageContext, config = { insideTable: false }) {
        switch (block.type) {
            // Following blocks are automatically transformed
            case DocumentationLegacyPageBlockType.callout:
                return this.convertCalloutBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.code:
                return this.convertCodeBlock(block);
            case DocumentationLegacyPageBlockType.componentAssets:
                return this.convertComponentAssetBlock(block);
            case DocumentationLegacyPageBlockType.custom:
                return this.convertCustomBlock();
            case DocumentationLegacyPageBlockType.divider:
                return this.convertDividerBlock();
            case DocumentationLegacyPageBlockType.figmaEmbed:
                return this.convertFigmaEmbedBlock(block);
            case DocumentationLegacyPageBlockType.figmaFrames:
                return this.convertFigmaFramesBlock(block);
            case DocumentationLegacyPageBlockType.genericEmbed:
                return this.convertGenericEmbedBlock(block);
            case DocumentationLegacyPageBlockType.heading:
                return this.convertHeadingBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.image:
                return this.convertImageBlock(block, config.insideTable);
            case DocumentationLegacyPageBlockType.link:
                return this.convertLinkBlock(block);
            case DocumentationLegacyPageBlockType.orderedList:
                return this.convertOrderedListBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.quote:
                return this.convertQuoteBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.renderCode:
                return this.convertLiveCodeBlock(block);
            case DocumentationLegacyPageBlockType.shortcuts:
                return this.convertShortcutsBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.storybookEmbed:
                return this.convertStorybookEmbedBlock(block);
            case DocumentationLegacyPageBlockType.text:
                return this.convertTextBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.token:
                return this.convertTokenBlock(block);
            case DocumentationLegacyPageBlockType.tokenGroup:
                return this.convertTokenGroupBlock(block);
            case DocumentationLegacyPageBlockType.tokenList:
                return this.convertTokenListBlock(block);
            case DocumentationLegacyPageBlockType.unorderedList:
                return this.convertUnorderedListBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.youtubeEmbed:
                return this.convertYoutubeEmbedBlock(block);
            // Following blocks are special because their transformation is invoked manually (containers)
            case DocumentationLegacyPageBlockType.column:
                return this.convertColumnBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.table:
                return this.convertTableBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tabs:
                return this.convertTabsBlock(block, pageContext);
            // Following blocks are special because their transformation is invoked manually (contained items)
            case DocumentationLegacyPageBlockType.tabItem:
                return this.convertTabItemBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.columnItem:
                return this.convertColumnItemBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tableCell:
                return this.convertTableCellBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tableRow:
                return this.convertTableRowBlock();
            case DocumentationLegacyPageBlockType.files:
                return this.convertFilesBlock(block);
            case DocumentationLegacyPageBlockType.figmaComponentPropsTable:
                return this.convertFigmaComponentPropsTableBlock(block);
            case DocumentationLegacyPageBlockType.figmaComponents:
                return this.convertFigmaComponentsBlock();
            case DocumentationLegacyPageBlockType.guidelines:
                return this.convertGuidelinesBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.guideline:
                // Individual guideline blocks should not be processed directly
                // They are always children of Guidelines containers and handled there
                return null;
            default:
                throw new Error("Unknown block type", { cause: block.type });
        }
    }
    // -- Plain blocks
    async convertHeadingBlock(block, pageContext) {
        await this.initialize();
        const heading = block;
        const text = await this.utilTransformer.convertTextBlockToMarkdown(heading, pageContext);
        switch (heading.headingType) {
            case DocumentationLegacyHeadingType.h1:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `# ${text}`;
            case DocumentationLegacyHeadingType.h2:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `## ${text}`;
            case DocumentationLegacyHeadingType.h3:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `### ${text}`;
            case DocumentationLegacyHeadingType.h4:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `#### ${text}`;
            case DocumentationLegacyHeadingType.h5:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `##### ${text}`;
            default:
                throw new Error("Unknown heading type", { cause: block.headingType });
        }
    }
    async convertCalloutBlock(block, pageContext) {
        await this.initialize();
        const text = await this.utilTransformer.convertTextBlockToMarkdown(block, pageContext);
        let calloutType;
        switch (block.calloutType) {
            case DocumentationLegacyCalloutType.info:
                calloutType = "ℹ️ Info";
                break;
            case DocumentationLegacyCalloutType.warning:
                calloutType = "⚠️ Warning";
                break;
            case DocumentationLegacyCalloutType.success:
                calloutType = "✅ Success";
                break;
            case DocumentationLegacyCalloutType.error:
                calloutType = "❌ Error";
                break;
            case DocumentationLegacyCalloutType.primary:
                calloutType = "💡 Important";
                break;
            default:
                throw new Error("Unknown callout type", { cause: block.calloutType });
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `> ${calloutType}: ${text === null || text === void 0 ? void 0 : text.replace(/\n\n/g, "  \n")}`;
    }
    async convertQuoteBlock(block, pageContext) {
        await this.initialize();
        const text = await this.utilTransformer.convertTextBlockToMarkdown(block, pageContext);
        // replace with two spaces at the end of the line, so markdown knows it's multiline quote
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `> ${text === null || text === void 0 ? void 0 : text.replace(/\n\n/g, "  \n")}`;
    }
    convertDividerBlock() {
        return "---";
    }
    convertImageBlock(block, insideTable = false) {
        var _a;
        if (block.url) {
            const imageAlt = (_a = block.caption) !== null && _a !== void 0 ? _a : "";
            const imageMarkdown = `![${imageAlt}](${block.url})`;
            if (block.caption && insideTable !== true) {
                return `${imageMarkdown}\n_${block.caption}_`;
            }
            return imageMarkdown;
        }
        return null;
    }
    async convertOrderedListBlock(block, pageContext) {
        await this.initialize();
        const text = await this.utilTransformer.convertTextBlockToMarkdown(block, pageContext);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `1. ${text}`;
    }
    async convertUnorderedListBlock(block, pageContext) {
        await this.initialize();
        const text = await this.utilTransformer.convertTextBlockToMarkdown(block, pageContext);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `- ${text}`;
    }
    convertLiveCodeBlock(block) {
        const codeLanguageDefinition = "javascript";
        return `\`\`\`${codeLanguageDefinition}\n${block.code}\n\`\`\``;
    }
    convertCodeBlock(block) {
        const codeLanguageDefinition = block.codeLanguage
            ? block.codeLanguage.toLowerCase()
            : "";
        return `\`\`\`${codeLanguageDefinition}\n${block.text.asPlainText()}\n\`\`\``;
    }
    async convertTextBlock(block, pageContext) {
        await this.initialize();
        return this.utilTransformer.convertTextBlockToMarkdown(block, pageContext);
    }
    convertGenericEmbedBlock(block) {
        return this.convertURLBlock(block, "Open link");
    }
    convertStorybookEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Storybook");
    }
    convertLinkBlock(block) {
        return this.convertURLBlock(block, "Open link");
    }
    convertYoutubeEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Youtube video");
    }
    convertFigmaEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Figma prototype / file");
    }
    // -- Token blocks
    async convertTokenBlock(block) {
        if (!block.tokenId) {
            return null;
        }
        // Fetch token
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        tokens = tokens.filter((t) => t.id === block.tokenId || t.idInVersion === block.tokenId);
        const firstToken = tokens[0];
        if (tokens.length === 0 || !firstToken) {
            return null;
        }
        // Convert token to table format
        return this.utilTransformer.convertTokensToMarkdownTable([firstToken]);
    }
    async convertTokenListBlock(block) {
        if (!block.tokenIds) {
            return null;
        }
        // Fetch tokens
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        tokens = tokens.filter((t) => block.tokenIds.includes(t.id) || block.tokenIds.includes(t.idInVersion));
        if (tokens.length === 0) {
            return null;
        }
        // Convert tokens to table format
        return this.utilTransformer.convertTokensToMarkdownTable(tokens);
    }
    async convertTokenGroupBlock(block) {
        if (!block.groupId) {
            return null;
        }
        // Fetch tokens
        const tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        let groups = await this.instance.tokens.getTokenGroups({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        groups = groups.filter((g) => g.id === block.groupId || g.idInVersion === block.groupId);
        const firstGroup = groups[0];
        if (groups.length !== 1 || !firstGroup) {
            return null;
        }
        // Show either single group or all nested groups
        let groupsToShow = [firstGroup];
        if (block.showNestedGroups) {
            groupsToShow = this.flattenedGroupsFromRoot(firstGroup, groups);
        }
        // Convert group and all its tokens
        const segments = [];
        for (const group of groupsToShow) {
            const tokensToShow = tokens.filter((t) => group.tokenIds.includes(t.id) ||
                group.tokenIds.includes(t.idInVersion));
            if (tokensToShow.length > 0) {
                const segment = `**Token Group ${[...group.path, group.name].join(" / ")}**:\n\n${this.utilTransformer.convertTokensToMarkdownTable(tokensToShow)}`;
                segments.push(segment);
            }
        }
        // Convert to single string
        return segments.join("\n\n");
    }
    flattenedGroupsFromRoot(root, allGroups) {
        let groups = [root];
        for (const groupId of root.subgroupIds) {
            const group = allGroups.find((g) => g.id === groupId);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            groups.push(group);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            groups = groups.concat(this.flattenedGroupsFromRoot(group, allGroups));
        }
        return groups;
    }
    async convertComponentAssetBlock(block) {
        const assets = await this.instance.assets.getAssets({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const convertedAssets = [];
        for (const asset of block.assets) {
            const assetObject = assets.filter((a) => a.id === asset.assetId)[0];
            if (assetObject && assetObject.thumbnailUrl) {
                convertedAssets.push(`![${assetObject.name}](${assetObject.thumbnailUrl})${"\n"}${asset.title ? asset.title : assetObject.name}${asset.description ? `, ${asset.description}${"\n"}` : ""}`);
            }
        }
        return convertedAssets.join("\n");
    }
    convertFigmaFramesBlock(block) {
        var _a, _b;
        const convertedFrames = [];
        for (const frame of block.frames) {
            if (!frame.previewUrl) {
                continue;
            }
            const title = (_a = frame.title) !== null && _a !== void 0 ? _a : "";
            const description = (_b = frame.description) !== null && _b !== void 0 ? _b : "";
            const frameMarkdownParts = [];
            // Add the image with double spaces for line break
            frameMarkdownParts.push(`![${title}](${frame.previewUrl})  `);
            // Add title on separate line if it exists - bold+italic
            if (title) {
                frameMarkdownParts.push(`***${title}***  `);
            }
            // Add description on separate line if it exists - italic
            if (description) {
                frameMarkdownParts.push(`*${description}*`);
            }
            const frameMarkdown = frameMarkdownParts.join("\n");
            convertedFrames.push(frameMarkdown);
        }
        return convertedFrames.join("\n\n");
    }
    async convertShortcutsBlock(block, pageContext) {
        var _a, _b, _c, _d;
        await this.initialize();
        const convertedShortcut = [];
        for (const shortcut of block.shortcuts) {
            const url = await this.linkResolver.resolveLink(shortcut.externalUrl, shortcut.internalId, pageContext);
            if (shortcut.previewUrl) {
                // Generate shortcut with image preview
                const previewUrl = shortcut.previewUrl;
                const title = (_a = shortcut.title) !== null && _a !== void 0 ? _a : undefined;
                const description = (_b = shortcut.description) !== null && _b !== void 0 ? _b : undefined;
                convertedShortcut.push(`[![${title}](${previewUrl})](${url})${"\n"}${title || ""}${description ? `, ${description}${"\n"}` : "\n"}`);
            }
            else {
                // Generate shortcut without image preview
                const title = (_c = shortcut.title) !== null && _c !== void 0 ? _c : undefined;
                const description = (_d = shortcut.description) !== null && _d !== void 0 ? _d : undefined;
                convertedShortcut.push(`[${title}](${url})${description ? `, ${description}${"\n"}` : "\n"}`);
            }
        }
        return convertedShortcut.join("\n");
    }
    relativePathToPage(page, currentPage) {
        // Compute level of depth
        let { parent } = currentPage;
        let depth = 0;
        while (parent) {
            depth += 1;
            // @ts-expect-error TS(2322): Type 'DocumentationLegacyGroup | null' is not assi... Remove this comment to see the full error message
            parent = parent.parent;
        }
        // Compute path
        const prefix = depth === 1 ? "./" : "../".repeat(depth - 1);
        let pageUrl = page.relativePageUrl();
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        if (pageUrl.endsWith("html")) {
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            pageUrl = `${pageUrl.substring(0, pageUrl.length - 4)}md`;
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return prefix + pageUrl;
    }
    // -- Containers: Table
    async convertTableBlock(block, pageContext) {
        // No empty tables
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        // Generate header row
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        const firstRow = block.children[0];
        const rowContent = [];
        const separatorContent = [];
        let count = 1;
        // TODO:fix-sdk-eslint
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const child of firstRow.children) {
            rowContent.push(`Column ${count}`);
            let separator = "---";
            // @ts-expect-error TS2367: Alignment type mismatch
            if (child.alignment === "Center") {
                separator = ":---:";
                // @ts-expect-error TS2367: Alignment type mismatch
            }
            else if (child.alignment === "Right") {
                separator = "---:";
            }
            else {
                separator = "---";
            }
            separatorContent.push(separator);
            count += 1;
        }
        const tableRows = [
            `| ${rowContent.join(" | ")} |`,
            `| ${separatorContent.join(" | ")} |`,
        ];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockTableRow) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const childContent = await Promise.all(
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                child.children.map((c) => this.convertBlockToMarkdown(c, pageContext, {
                    insideTable: true,
                })));
                const rowDefinition = `| ${childContent.join(" | ")} |`;
                tableRows.push(rowDefinition);
            }
        }
        return tableRows.join("\n");
    }
    convertTableRowBlock() {
        // Not used, rows are used when generating table directly in the table
        return null;
    }
    async convertTableCellBlock(block, pageContext) {
        const cellContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext, {
            insideTable: true,
        })));
        return cellContent.join("<br>"); // Multiline cell needs non-regular line breaks
    }
    // -- Containers: Tabs
    async convertTabsBlock(block, pageContext) {
        // No empty tabs
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        const tabContent = [];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockTabItem) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const result = await this.convertTabItemBlock(child, pageContext);
                // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                tabContent.push(result);
            }
        }
        return `---\n\n${tabContent.join("\n\n")}\n\n---`;
    }
    async convertTabItemBlock(block, pageContext) {
        const childContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
        const tabDefinition = `**${block.caption}**\n\n${childContent.join("\n\n")}`;
        return tabDefinition;
    }
    // -- Containers: Columns
    async convertColumnBlock(block, pageContext) {
        // No empty columns
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        const columnContent = [];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockColumnItem) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const result = await this.convertColumnItemBlock(child, pageContext);
                // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                columnContent.push(result);
            }
        }
        return columnContent.join("\n");
    }
    async convertColumnItemBlock(block, pageContext) {
        const childContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
        const tabDefinition = childContent.join("\n\n");
        return tabDefinition;
    }
    // -- Unsupported
    convertCustomBlock() {
        // Custom blocks will not be transformed for now, as we can't realistically
        // know what they are supposed to do and right now we also can't invoke them
        return null;
        // TODO: Custom component blocks should still be converted, probably
    }
    // -- Conveniences
    convertURLBlock(block, userAction) {
        // Will generate:
        // [Action prompt, ie. "Open Figma File"](url)
        // ^ caption_if_provided
        if (block.url) {
            const caption = block.caption ? `\n^ ${block.caption}` : "";
            return `[${userAction}](${block.url})${caption}`;
        }
        return null;
    }
    async convertFilesBlock(block) {
        var _a, _b;
        if (!block.files || block.files.length === 0) {
            return null;
        }
        // Fetch all files from the version
        const allFiles = await this.instance.resources.getFiles({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const convertedFiles = [];
        for (const fileRef of block.files) {
            // Find the file by entityId (which maps to referencePersistentId)
            const fileObject = allFiles.find((f) => f.id === fileRef.entityId);
            if (fileObject && fileObject.url) {
                // Use custom title/description if provided, otherwise fall back to file name
                const title = ((_a = fileRef.entityMeta) === null || _a === void 0 ? void 0 : _a.title) || fileObject.name;
                const description = (_b = fileRef.entityMeta) === null || _b === void 0 ? void 0 : _b.description;
                // Create markdown link for file download
                let fileMarkdown = `[📥 ${title}](${fileObject.url})`;
                // Add description if available
                if (description) {
                    fileMarkdown += `${"\n"}_${description}_`;
                }
                convertedFiles.push(fileMarkdown);
            }
        }
        if (convertedFiles.length === 0) {
            return null;
        }
        return convertedFiles.join("\n");
    }
    async convertFigmaComponentPropsTableBlock(block) {
        var _a;
        if (!block.selectedFigmaComponent) {
            return null;
        }
        // Fetch all design components from the version
        const designComponents = await this.instance.components.getDesignComponents({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        // Find the component by figmaComponentId
        const component = designComponents.find((c) => { var _a; return c.id === ((_a = block.selectedFigmaComponent) === null || _a === void 0 ? void 0 : _a.figmaComponentId); });
        if (!component || !component.componentPropertyDefinitions) {
            return null;
        }
        // Get selected properties or all properties if none specified
        const selectedProperties = block.selectedFigmaComponent.selectedComponentProperties;
        let propertiesToShow = (selectedProperties === null || selectedProperties === void 0 ? void 0 : selectedProperties.length) > 0
            ? component.componentPropertyDefinitions.filter((prop) => selectedProperties.includes(prop.id))
            : component.componentPropertyDefinitions;
        if (propertiesToShow.length === 0) {
            return null;
        }
        // Sort properties: Variants first, then the rest in original order
        const variantProperties = propertiesToShow.filter((prop) => prop.type === "Variant");
        const otherProperties = propertiesToShow.filter((prop) => prop.type !== "Variant");
        propertiesToShow = [...variantProperties, ...otherProperties];
        // Create markdown table
        const tableRows = [
            "| Property | Type | Default Value |",
            "| --- | --- | --- |",
        ];
        for (const property of propertiesToShow) {
            const propertyName = property.name;
            const propertyType = property.type;
            let defaultValue = "";
            // Format default value based on property type
            switch (property.type) {
                case "Boolean":
                    defaultValue = property.defaultValue ? "true" : "false";
                    break;
                case "Text":
                    defaultValue = `"${property.defaultValue}"`;
                    break;
                case "Variant":
                    defaultValue = ((_a = property.options) === null || _a === void 0 ? void 0 : _a.includes(property.defaultValue))
                        ? `"${property.defaultValue}"`
                        : "N/A";
                    break;
                case "InstanceSwap":
                    defaultValue = "";
                    break;
                default:
                    defaultValue = String(property.defaultValue || "N/A");
            }
            tableRows.push(`| ${propertyName} | ${propertyType} | ${defaultValue} |`);
        }
        // Add component name as H3 header
        let result = `### ${component.name}\n`;
        result += tableRows.join("\n");
        return result;
    }
    async convertGuidelinesBlock(block, pageContext) {
        // Check if there are any child guidelines
        if (!block.children || block.children.length === 0) {
            return null;
        }
        await this.initialize();
        // Create table header
        const tableRows = [
            "| Type | Image | Description |",
            "| --- | --- | --- |",
        ];
        for (const child of block.children) {
            if (child.type === DocumentationLegacyPageBlockType.guideline) {
                const guidelineBlock = child;
                const { guideline } = guidelineBlock;
                const { asset, type, imageAlt, imageCaption } = guideline;
                // Determine guideline type emoji and label
                let typeLabel = "";
                let emoji = "";
                switch (type.toLowerCase()) {
                    case "do":
                        emoji = "✅";
                        typeLabel = "Do";
                        break;
                    case "dont":
                        emoji = "❌";
                        typeLabel = "Don't";
                        break;
                    case "caution":
                        emoji = "⚠️";
                        typeLabel = "Caution";
                        break;
                    default:
                        emoji = "💡";
                        typeLabel = "Guideline";
                }
                // Format image column
                let imageColumn = "";
                if (asset && asset.url) {
                    const altText = imageAlt || typeLabel;
                    imageColumn = `![${altText}](${asset.url})`;
                    // Add image caption if provided
                    if (imageCaption) {
                        imageColumn += `<br>_${imageCaption}_`;
                    }
                }
                // Format description column - get content from child blocks
                let descriptionColumn = "";
                if (guidelineBlock.children && guidelineBlock.children.length > 0) {
                    const childContent = await Promise.all(
                    // TODO:fix-sdk-eslint
                    // eslint-disable-next-line no-await-in-loop
                    guidelineBlock.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
                    // Filter out null values from child content conversion
                    // Some blocks may return null if they can't be converted or are empty
                    const validContent = childContent.filter((content) => content !== null);
                    if (validContent.length > 0) {
                        descriptionColumn = validContent
                            .join("<br>")
                            .replace(/\|/g, "\\|") // Escape pipe characters for table compatibility
                            .replace(/\n/g, "<br>"); // Replace line breaks with <br> for table cells
                    }
                }
                // Create table row
                const typeColumn = `${emoji} **${typeLabel}**`;
                tableRows.push(`| ${typeColumn} | ${imageColumn} | ${descriptionColumn} |`);
            }
        }
        if (tableRows.length <= 2) {
            // Only header rows, no actual guidelines
            return null;
        }
        return tableRows.join("\n");
    }
    convertFigmaComponentsBlock() {
        // Figma components block is not supported in markdown conversion
        return "";
    }
}
